<template>
    <StandardLayout>
        <div class="container-wrap">
            <DoubleHeader
                :backRoute="projectId ? 'viewStation' : 'viewStationFromMap'"
                :title="$tc('station.allPhotos')"
                :backTitle="$tc('layout.backStationDashboard')"
                :backRouteParams="{ projectId, stationId }"
            >
                <template v-slot:default v-if="!readOnly">
                    <button class="button-social">
                        <label for="imageInput">
                            <i class="icon icon-photo"></i>
                            {{ $tc("station.btn.addPhotos") }}
                        </label>
                    </button>
                    <input id="imageInput" type="file" accept="image/jpeg, image/png" @change="upload" />
                </template>
            </DoubleHeader>

            <SilentboxMediaGallery v-if="photos && photos.length > 0" :photos="photos">
                <template #item-actions="{ photo }">
                    <button class="photo-options" v-if="!readOnly">
                        <ListItemOptions :options="photoOptions" @listItemOptionClick="onPhotoOptionClick($event, photo)"></ListItemOptions>
                    </button>
                </template>
            </SilentboxMediaGallery>

            <div v-else class="empty-photos">
                {{ $t("station.emptyPhotos") }}
            </div>
        </div>
    </StandardLayout>
</template>

<script lang="ts">
import Vue from "vue";
import StandardLayout from "@/views/StandardLayout.vue";
import DoubleHeader from "@/views/shared/DoubleHeader.vue";
import SilentboxMediaGallery from "@/views/shared/SilentboxMediaGallery.vue";
import { AddedPhoto, NoteMedia, PortalNoteMedia } from "@/views/notes/model";
import { ActionTypes } from "@/store";
import ListItemOptions, { ListItemOption } from "@/views/shared/ListItemOptions.vue";
import { SnackbarStyle } from "@/store/modules/snackbar";

export default Vue.extend({
    name: "StationPhotosView",
    components: {
        StandardLayout,
        DoubleHeader,
        SilentboxMediaGallery,
        ListItemOptions,
    },
    computed: {
        projectId(): number {
            return parseInt(this.$route.params.projectId, 10);
        },
        stationId(): number {
            return parseInt(this.$route.params.stationId, 10);
        },
        photos(): NoteMedia[] {
            if (this.$state.notes.media) {
                return NoteMedia.onlyPhotos(this.$state.notes.media);
            }
            return [];
        },
        media(): PortalNoteMedia[] {
            return this.$state.notes.media;
        },
        readOnly(): boolean {
            return this.$state.notes.readOnly;
        },
    },
    data: (): {
        photoOptions: ListItemOption[];
        loadingPhotoId: number | null;
    } => {
        return {
            photoOptions: [],
            loadingPhotoId: null,
        };
    },
    methods: {
        async onPhotoOptionClick(event: string, photo: PortalNoteMedia) {
            if (event === "delete-photo") {
                await this.$confirm({
                    message: this.$tc("confirmDeletePhoto"),
                    button: {
                        no: this.$tc("cancel"),
                        yes: this.$tc("delete"),
                    },
                    callback: async (confirm) => {
                        if (confirm) {
                            this.loadingPhotoId = photo.id;
                            this.$services.api
                                .deleteMedia(photo.id)
                                .then(async () => {
                                    await this.$store.dispatch(ActionTypes.NEED_NOTES, { id: this.$route.params.stationId });
                                    await this.$store.dispatch(ActionTypes.CLEAR_STATION, this.$route.params.stationId);
                                })
                                .finally(() => {
                                    this.onFinishedPhotoAction(this.$tc("successDeletePhoto"), SnackbarStyle.success);
                                })
                                .catch(() => {
                                    this.onFinishedPhotoAction(this.$tc("somethingWentWrong"), SnackbarStyle.fail);
                                });
                        }
                    },
                });
            }
            if (event === "set-as-station-image") {
                this.loadingPhotoId = photo.id;
                this.$services.api
                    .setStationImage(this.stationId, photo.id)
                    .then(() => {
                        this.onFinishedPhotoAction(this.$tc("successSetAsStationPhoto"), SnackbarStyle.success);
                        this.$store.dispatch(ActionTypes.NEED_NOTES, { id: this.$route.params.stationId });
                    })
                    .catch(() => {
                        this.onFinishedPhotoAction(this.$tc("somethingWentWrong"), SnackbarStyle.fail);
                    });
            }
        },
        onFinishedPhotoAction(message: string, type: SnackbarStyle): void {
            this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, { message, type });
            this.loadingPhotoId = null;
        },
        upload(this: any, ev) {
            const image = ev.target.files[0];

            const reader = new FileReader();
            reader.onload = (ev) => {
                if (ev?.target?.result) {
                    const photo = new AddedPhoto(image.type, image, ev.target.result);
                    this.$services.api
                        .uploadStationMedia(this.stationId, photo.key, photo.file)
                        .then(() => {
                            this.$store.dispatch(ActionTypes.NEED_NOTES, { id: this.stationId });
                            this.onFinishedPhotoAction(this.$tc("successAddedPhoto"), SnackbarStyle.success);
                            return [];
                        })
                        .catch(() => {
                            this.onFinishedPhotoAction(this.$tc("somethingWentWrong"), SnackbarStyle.fail);
                        });
                }
            };

            reader.readAsDataURL(image);
        },
    },
    mounted() {
        this.photoOptions = [
            {
                label: this.$tc("station.btn.setAsStationImage"),
                event: "set-as-station-image",
                icon: "icon-home",
            },
            {
                label: this.$tc("station.btn.deletePhoto"),
                event: "delete-photo",
                icon: "icon-trash",
            },
        ];
    },
    beforeMount(): Promise<any> {
        return this.$store.dispatch(ActionTypes.NEED_NOTES, { id: this.stationId });
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/mixins";
@use "src/scss/variables";

.photo-options {
    @include mixins.position(absolute, 20px 20px null null);
    width: 35px;
    height: 35px;
    background-color: #fff;
    border: solid 1px #cccdcf;
    padding: 0;
    border-radius: 50px;
    z-index: variables.$z-index-top;

    ::v-deep .options-trigger {
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: -6px;
        height: 100%;

        &:after {
            transform: translate(-1px, 4px);
            font-weight: bold;
        }
    }

    ::v-deep .options-btns {
        right: 30px;
        left: unset;
        top: 30px;
        border: solid 1px var(--color-border);
        border-radius: 1px;
        padding-right: 25px;

        > * {
            font-size: 14px;
        }
    }
}

input[type="file"] {
    display: none;
}

.button-social {
    padding: 0 20px;

    label {
        display: flex;
        align-items: center;
        cursor: pointer;
    }
}

.empty-photos {
    margin-top: 10px;
}

::v-deep #silentbox-overlay__arrow-buttons {
    @include mixins.bp-down(variables.$md) {
        .arrow-previous {
            left: 30px;
        }
        .arrow-next {
            right: 30px;
        }
    }

    @include mixins.bp-down(variables.$xs) {
        .arrow-previous {
            left: 15px;
        }
        .arrow-next {
            right: 15px;
        }
    }
}

::v-deep #silentbox-overlay__close-button .icon {
    @include mixins.bp-down(variables.$md) {
        left: 35px;
        top: -20px;
    }

    @include mixins.bp-down(variables.$xs) {
        left: 45px;
        top: -40px;
    }
}
</style>
